import { postmortemApiEndpoint } from "../config";
import { useFetchWithRetry } from "../Utils/fetchWithRetry";
import { logError, logInfo } from "../Utils/loggers";
import { PutWithRetry } from "../Utils/putWithRetry";
import axios from "axios";
import { fetchToken } from "./fetchTokenService";
import { getConsumerKey } from "./maerskApiTokenService";
import { loginRequest } from "../auth/authConfig";
import { useQueryClient } from "react-query";

// ==================================================================================
// DEPRECATED HARDCODED LISTS - DO NOT USE FOR AUTHORIZATION
// ==================================================================================
// These lists are OUTDATED and do NOT reflect the actual approvers from backend.
// The backend dynamically determines approvers from PortfolioMappingConfig based 
// on Portfolio + Platform, which includes multiple EDs per platform.
// 
// CORRECT APPROACH: Always use the dynamic `leadershipReviewApprovers` list from 
// the backend's approval status API response for authorization checks.
// 
// These are kept only for backward compatibility and reference.
// ==================================================================================
const ED_APPROVERS = ["k.harish@maersk.com"];  // ❌ INCOMPLETE - Missing many Platform Owner EDs

const TLT_APPROVERS_BY_PORTFOLIO = {
  "APMT, IOT and Automation": "nilesh.chakkar@maersk.com",
  "Ocean & Logistics": "nilesh.chakkar@maersk.com",
  "Terminal (O&L)": "nilesh.chakkar@maersk.com",
  "B&F & People": "nilesh.chakkar@maersk.com",
  "Default": "nilesh.chakkar@maersk.com",
};

/**
 * ❌ DEPRECATED - DO NOT USE
 * Get all leadership approvers (ED + TLT) for a portfolio
 * This function uses hardcoded lists and is INACCURATE.
 * Use the dynamic `leadershipReviewApprovers` from approval status API instead.
 */
export const getLeadershipApprovers = (portfolio) => {
  const tltApprover = TLT_APPROVERS_BY_PORTFOLIO[portfolio] || TLT_APPROVERS_BY_PORTFOLIO["Default"];
  return [...ED_APPROVERS, tltApprover];
};

/**
 * ❌ DEPRECATED - DO NOT USE
 * Check if user is an authorized approver
 * This function uses hardcoded lists and is INACCURATE.
 * Use the dynamic `leadershipReviewApprovers` from approval status API instead.
 */
export const isAuthorizedApprover = (userEmail, portfolio) => {
  const allApprovers = getLeadershipApprovers(portfolio);
  return allApprovers.includes(userEmail);
};

/**
 * Service for Approval Workflow operations (Simplified - Single Leadership Review)
 */
export const ApprovalWorkflowServices = () => {
  const { fetchWithRetry } = useFetchWithRetry();
  const { putWithRetry } = PutWithRetry();
  const queryClient = useQueryClient();
  const invalidateApprovalStatus = (postMortemId) => {
    queryClient.invalidateQueries(["approvalStatus", postMortemId]);
    queryClient.invalidateQueries(["postmortem"]);
  };

  /**
   * Submit postmortem for Leadership Review (ED + TLT combined)
   */
  const submitForLeadershipReview = async (instance, accounts, postMortemId, submittedBy) => {
    logInfo("submitForLeadershipReview", `Submitting postmortem ${postMortemId} for Leadership Review`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/submit-for-review`;

      // Get Azure AD auth token (Maersk API token not needed for approval workflow)
      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const config = {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
          "X-User": accounts[0]?.username || accounts[0]?.name || submittedBy || "unknown",
        },
      };

      const response = await axios.post(url, { submittedBy }, config);
      invalidateApprovalStatus(postMortemId);
      logInfo("submitForLeadershipReview", "Successfully submitted for Leadership Review");
      return response.data;
    } catch (error) {
      logError("submitForLeadershipReview", "Error submitting for Leadership Review", error);
      throw error;
    }
  };

  /**
   * Approve Leadership Review (first approver wins - ED or TLT)
   */
  const approveLeadershipReview = async (instance, accounts, postMortemId, comment) => {
    logInfo("approveLeadershipReview", `Approving Leadership Review for ${postMortemId} with comment: '${comment || ''}'`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/approve`;

      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const config = {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
          "X-User": accounts[0]?.username || accounts[0]?.name || "unknown",
        },
      };

      const response = await axios.post(url, { comment: comment || "" }, config);
      invalidateApprovalStatus(postMortemId);
      logInfo("approveLeadershipReview", "Successfully approved Leadership Review");
      return response.data;
    } catch (error) {
      logError("approveLeadershipReview", "Error approving Leadership Review", error);
      throw error;
    }
  };

  /**
   * Reject Leadership Review - returns to DRAFT
   */
  const rejectLeadershipReview = async (instance, accounts, postMortemId, comment) => {
    logInfo("rejectLeadershipReview", `Rejecting Leadership Review for ${postMortemId}`);
    try {
      if (!comment || comment.trim().length === 0) {
        throw new Error("Comment is required for rejection");
      }

      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/reject`;

      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const config = {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
          "X-User": accounts[0]?.username || accounts[0]?.name || "unknown",
        },
      };

      const response = await axios.post(url, { comment }, config);
      invalidateApprovalStatus(postMortemId);
      logInfo("rejectLeadershipReview", "Successfully rejected Leadership Review");
      return response.data;
    } catch (error) {
      logError("rejectLeadershipReview", "Error rejecting Leadership Review", error);
      throw error;
    }
  };

  /**
   * Get approval status
   */
  const getApprovalStatus = async (postMortemId) => {
    logInfo("getApprovalStatus", `Getting approval status for ${postMortemId}`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/approval-status`;
      const data = await fetchWithRetry(url);
      logInfo("getApprovalStatus", "Successfully fetched approval status");
      return data;
    } catch (error) {
      logError("getApprovalStatus", "Error getting approval status", error);
      throw error;
    }
  };

  /**
   * Get approval history
   */
  const getApprovalHistory = async (postMortemId) => {
    logInfo("getApprovalHistory", `Getting approval history for ${postMortemId}`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/approval-history`;
      const data = await fetchWithRetry(url);
      logInfo("getApprovalHistory", "Successfully fetched approval history");
      return data;
    } catch (error) {
      logError("getApprovalHistory", "Error getting approval history", error);
      throw error;
    }
  };

  /**
   * Save approval comment (dedicated endpoint for reliable comment storage)
   */
  const saveApprovalComment = async (instance, accounts, postMortemId, comment, savedBy) => {
    logInfo("saveApprovalComment", `Saving comment for ${postMortemId}: '${comment || ''}'`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/save-comment`;

      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const config = {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
          "X-User": accounts[0]?.username || accounts[0]?.name || savedBy || "unknown",
        },
      };

      const response = await axios.post(url, { comment: comment || "", savedBy }, config);
      invalidateApprovalStatus(postMortemId);
      logInfo("saveApprovalComment", `Successfully saved comment: '${comment || ''}'`);
      return response.data;
    } catch (error) {
      logError("saveApprovalComment", "Error saving comment", error);
      throw error;
    }
  };

  /**
   * Preview who will receive the Leadership Review email (Platform Owners + Portfolio/TLT Owner).
   * Read-only — does not send anything or change status.
   */
  const getRecipientsPreview = async (postMortemId) => {
    logInfo("getRecipientsPreview", `Getting recipients preview for ${postMortemId}`);
    try {
      const url = `${postmortemApiEndpoint}/approval-workflow/${postMortemId}/recipients-preview`;
      const data = await fetchWithRetry(url);
      return data;
    } catch (error) {
      logError("getRecipientsPreview", "Error getting recipients preview", error);
      throw error;
    }
  };

  /**
   * Save the user-edited recipient list. Writes the full list into BOTH manual
   * override fields so additions/removals are honored consistently:
   *  - manual-platform-owners: full list, comma-joined
   *  - manual-portfolio-owner: first email of that same list
   */
  const saveManualRecipients = async (instance, accounts, postMortemId, recipients) => {
    logInfo("saveManualRecipients", `Saving ${recipients.length} recipient(s) for ${postMortemId}`);
    try {
      const tokenResponse = await fetchToken(instance, loginRequest, accounts);
      const config = {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
        },
      };

      const platformOwnersUrl = `${postmortemApiEndpoint}/postmortem-enhancements/${postMortemId}/manual-platform-owners`;
      const portfolioOwnerUrl = `${postmortemApiEndpoint}/postmortem-enhancements/${postMortemId}/manual-portfolio-owner`;

      await axios.put(platformOwnersUrl, { manualPlatformOwnerEmails: recipients.join(",") }, config);
      await axios.put(portfolioOwnerUrl, { manualPortfolioOwnerEmail: recipients[0] || "" }, config);

      logInfo("saveManualRecipients", "Successfully saved manual recipients");
    } catch (error) {
      logError("saveManualRecipients", "Error saving manual recipients", error);
      throw error;
    }
  };

  return {
    submitForLeadershipReview,
    approveLeadershipReview,
    rejectLeadershipReview,
    getApprovalStatus,
    getApprovalHistory,
    saveApprovalComment,
    getRecipientsPreview,
    saveManualRecipients,
  };
};
